import React, { useEffect, useRef, useState } from "react";
import { Pause, Play, X } from "lucide-react";

const GOLD = "#D49B3C";
const GOLD_DARK = "#A9712A";
const NAVY = "#1B2A3A";

const TICK_MS = 2000; // each count (3, 2, 1) holds for 2s — time to tap a guess

/**
 * The quiz start for every `/quiz` visitor — there is no start button.
 *
 * Slides up over the intro: "Call your shot" + the 8 persona chips +
 * a big 3-2-1 countdown. When it hits zero the quiz starts
 * automatically (mode="auto"). Pause freezes the sequence; Resume
 * restarts it from 3. Dismiss falls back to the classic intro.
 *
 * The ticker is frozen while the tab is hidden, so a prefetched or
 * background page load can never fire a phantom `start` funnel event.
 */
export const CountdownStart = ({ personas, guess, onGuess, onStart, onDismiss }) => {
  const [count, setCount] = useState(3);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(() => document.hidden);
  const timerRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (paused || hidden) return undefined;
    if (count <= 0) {
      if (!startedRef.current) {
        startedRef.current = true;
        onStart("auto");
      }
      return undefined;
    }
    timerRef.current = setTimeout(() => setCount((c) => c - 1), TICK_MS);
    return () => clearTimeout(timerRef.current);
  }, [count, paused, hidden, onStart]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-3 pb-3 sm:p-6"
      style={{ background: "rgba(16, 25, 35, 0.62)", backdropFilter: "blur(3px)" }}
      data-testid="countdown-overlay"
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-center animate-in slide-in-from-bottom-8 fade-in duration-500"
        role="dialog"
        aria-label="Guess your MoneyMind — quiz starting"
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Close and browse the intro instead"
          className="absolute-none float-right -mt-2 -mr-2 p-2 rounded-full text-[#9b9892] hover:text-[#2C302E] hover:bg-[#F4F1E8] transition-colors"
          data-testid="countdown-dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <span
          className="inline-block px-4 py-1.5 rounded-full text-[11px] font-semibold tracking-widest uppercase"
          style={{ background: "#FAEACB", color: GOLD_DARK }}
        >
          Monthly Contest
        </span>
        <h2 className="font-serif text-3xl sm:text-4xl mt-3" style={{ color: NAVY }}>
          Call your shot
        </h2>
        <p className="mt-2 text-sm text-[#4d4f4c]">
          Select your money personality — the quiz is starting in
        </p>

        {/* Big count */}
        <div
          key={paused ? "paused" : count}
          className="font-serif leading-none mt-3 select-none animate-in zoom-in-50 duration-300"
          style={{ fontSize: "5rem", color: paused ? "#9b9892" : GOLD_DARK }}
          data-testid="countdown-number"
        >
          {count > 0 ? count : "GO"}
        </div>

        {/* Persona chips */}
        <div className="mt-4 grid grid-cols-4 gap-2">
          {personas.map((p) => {
            const active = guess === p.slug;
            return (
              <button
                key={p.slug}
                type="button"
                onClick={() => onGuess(active ? null : p.slug)}
                className={`rounded-lg border px-1 py-2 text-center transition-colors ${
                  active
                    ? "border-transparent text-white"
                    : "border-[#E6E3DB] bg-[#FBFAF7] text-[#2C302E] hover:border-[#D49B3C]"
                }`}
                style={active ? { background: GOLD_DARK } : undefined}
                data-testid={`countdown-guess-${p.slug}`}
                aria-pressed={active}
              >
                <span className="block text-xl" aria-hidden>{p.icon}</span>
                <span className="block mt-0.5 text-[10px] font-medium leading-tight">
                  {p.name.replace("The ", "")}
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-[#9b9892]">
          {guess
            ? "Locked in — guess right and you're in this month's drawing."
            : "Optional — guess right to enter this month's drawing ($120 value)."}
        </p>

        {/* Controls — no start button by design; zero starts the quiz */}
        <div className="mt-4 flex items-center justify-center gap-3">
          {paused ? (
            <button
              type="button"
              onClick={() => { setCount(3); setPaused(false); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#E6E3DB] text-[#2C302E] hover:border-[#D49B3C] transition-colors"
              data-testid="countdown-resume"
            >
              <Play className="h-4 w-4" /> Restart 3·2·1
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { clearTimeout(timerRef.current); setPaused(true); }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-[#E6E3DB] text-[#2C302E] hover:border-[#D49B3C] transition-colors"
              data-testid="countdown-pause"
            >
              <Pause className="h-4 w-4" /> I need a sec
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CountdownStart;
